/**
 * modularPatientSessionState.js
 *
 * Centralises the clear-on-logout behaviour for the localStorage caches used by
 * the subcollection-based ("modular") assessment pathways (Adult Autism today,
 * future CYP modules). These caches exist purely for instant paint on a cold
 * reload of the SAME patient, mirroring the ADHD `patientDataModel` cache.
 *
 * WHY THIS EXISTS
 * ----------------
 * The ADHD contexts clear their localStorage on sign-out via
 * PatientDataContext.clearPatientData() (removes `patientDataModel` /
 * `loadedPatientId`). The modular contexts each own an equivalent clearData()
 * method, but those are only invoked on access errors / patient switches — NOT
 * on logout or token expiry. That left an autism patient's cached header and
 * form data sitting in localStorage at rest after a clinician signs out, which
 * on a shared Owl Centre machine is a PII exposure.
 *
 * This helper brings the modular pathways into parity with ADHD by wiping those
 * caches from the same GlobalStateContext de-auth branch that already clears the
 * dashboard view-state and Adult Autism History session state. (The next fetch
 * always re-checks Firestore access before rendering, so this is defence at
 * rest, not a correctness fix — but it matches the ADHD privacy guarantee.)
 *
 * The keys swept here are owned by:
 *   - `td_modular_patient_data` / `td_modular_loaded_patient_id`
 *       → src/context/ModularPatientDataContext.js (provider view)
 *   - `td_modular_self_patient_data`
 *       → src/context/ModularPatientSelfDataContext.js (patient self view)
 *   - `td_modular_self_informant_data`
 *       → src/context/ModularInformantSelfDataContext.js (informant self view)
 *
 * All of these share the `td_modular` prefix, so we sweep by prefix rather than
 * listing each key. This is deliberately drift-proof: any future modular cache
 * that follows the `td_modular*` naming convention is wiped automatically
 * without having to remember to update this helper.
 */

// Shared prefix for every modular-pathway localStorage cache key. Any key added
// by a modular context MUST keep this prefix so it is cleared on sign-out.
const MODULAR_STORAGE_PREFIX = 'td_modular';

/**
 * Remove every modular-pathway localStorage cache.
 *
 * Sweeps all keys under the `td_modular` prefix (across any patient/informant
 * seen in this browser session). Safe to call when localStorage is unavailable
 * (e.g. privacy mode) — it simply no-ops.
 */
export function clearModularPatientLocalStorage() {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(MODULAR_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // localStorage unavailable (e.g. privacy mode) — nothing to clear.
  }
}
